import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Box,
  Paper,
  Alert,
} from '@mui/material';
import {
  Warning,
  Person,
  CheckCircle,
  Cancel,
  Error,
} from '@mui/icons-material';
import CustomButton from '../../../../../../lib/designSystem/Button';
import { DeleteApprovalDialogProps } from './types';

const DeleteApprovalDialog = ({
  open,
  setOpen,
  approval,
  onConfirm,
  onCancel,
}: DeleteApprovalDialogProps) => {
  const [loading, setLoading] = useState(false);

  const handleConfirm = async () => {
    if (!approval.id) {
      console.error('No approval ID found');
      alert('Error: No se encontró el ID de la aprobación');
      return;
    }

    setLoading(true);
    try {
      await onConfirm(approval.id);
      setOpen(false);
    } catch (error) {
      console.error('Error al eliminar la aprobación:', error);
      alert('Error al eliminar la aprobación: ' + error);
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    onCancel();
    setOpen(false);
  };

  const getStatusIcon = () => {
    if (approval.status === 'Aprobada') {
      return <CheckCircle sx={{ fontSize: '1.2rem', color: 'success.main' }} />;
    } else if (approval.status === 'Rechazada') {
      return <Cancel sx={{ fontSize: '1.2rem', color: 'error.main' }} />;
    }
    return null;
  };

  return (
    <Dialog
      open={open}
      onClose={handleCancel}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
        },
      }}
    >
      <DialogTitle
        sx={{
          pb: 2,
          display: 'flex',
          alignItems: 'center',
          gap: 2,
        }}
      >
        <Box
          sx={{
            backgroundColor: 'rgba(255, 152, 0, 0.1)',
            borderRadius: '50%',
            p: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Warning sx={{ fontSize: '1.2rem', color: 'warning.main' }} />
        </Box>
        <Typography variant="h6" sx={{ fontWeight: 600 }}>
          Eliminar Aprobación
        </Typography>
      </DialogTitle>

      <DialogContent sx={{ pt: 0 }}>
        <Alert
          severity="warning"
          sx={{
            mb: 3,
            '& .MuiAlert-icon': {
              alignItems: 'center',
            },
          }}
          icon={<Error sx={{ fontSize: '1.2rem' }} />}
        >
          <Box>
            <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
              Acción Irreversible
            </Typography>
            <Typography variant="body2">
              Esta acción eliminará permanentemente la aprobación y no se puede
              deshacer. El proceso de revisión deberá continuar sin esta
              aprobación.
            </Typography>
          </Box>
        </Alert>

        <Typography variant="body2" sx={{ mb: 3, color: 'text.secondary' }}>
          ¿Estás seguro de que deseas eliminar la siguiente aprobación?
        </Typography>

        <Paper
          sx={{
            p: 2,
            mb: 3,
            boxShadow: 'none',
            border: '1px solid #e0e0e0',
            backgroundColor: '#fafafa',
            borderRadius: 1.5,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1.5 }}>
            <Box
              sx={{
                backgroundColor: 'rgba(156, 39, 176, 0.1)',
                borderRadius: '50%',
                p: 0.75,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Person sx={{ fontSize: '1rem', color: 'purple.main' }} />
            </Box>
            <Box>
              <Typography
                variant="body1"
                sx={{ fontWeight: 600, lineHeight: 1.2 }}
              >
                {approval.user}
              </Typography>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ fontSize: '0.85rem' }}
              >
                {approval.userEmail}
              </Typography>
            </Box>
          </Box>

          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              ml: 4.5,
              mb: 1,
            }}
          >
            {getStatusIcon()}
            <Typography variant="body2" sx={{ fontWeight: 500 }}>
              {approval.status} - {approval.role}
            </Typography>
          </Box>

          {approval.reason && (
            <Box sx={{ ml: 4.5 }}>
              <Typography
                variant="body2"
                sx={{
                  fontStyle: 'italic',
                  color: 'text.secondary',
                  backgroundColor: 'rgba(0, 0, 0, 0.03)',
                  p: 1,
                  borderRadius: 1,
                  borderLeft: '3px solid #e0e0e0',
                }}
              >
                &ldquo;{approval.reason}&rdquo;
              </Typography>
            </Box>
          )}
        </Paper>
      </DialogContent>

      <DialogActions sx={{ p: 2, gap: 1 }}>
        <CustomButton
          onClick={handleCancel}
          color="secondary"
          disabled={loading}
        >
          Cancelar
        </CustomButton>
        <CustomButton onClick={handleConfirm} color="error" disabled={loading}>
          {loading ? 'Eliminando...' : 'Eliminar Aprobación'}
        </CustomButton>
      </DialogActions>
    </Dialog>
  );
};

export default DeleteApprovalDialog;
